import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl, defineMessages} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    useCustomer,
    useCustomerId,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {} from '@salesforce/commerce-sdk-react'

import {
    Box,
    Heading,
    Text,
    Stack,
    Flex,
    Button,
    useDisclosure,
    Alert,
    AlertIcon
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Field from '../../components/field'
import Link from '@salesforce/retail-react-app/app/components/link'
import {LeftArrow} from '../../components/icons'
import useCommunicationsFields from '../../components/forms/useCommunicationsFields'
import SMSOptInModal from './sms-optin-modal'
import {useForm} from 'react-hook-form'
import {sendViewErrorEvent} from '../../utils/gtm-utils'
import usedDeviceDetection from '../../hooks/use-device-detection'

const CommunicationPreferences = ({prefix = ''}) => {
    const {formatMessage} = useIntl()
    const {isOpen, onOpen, onClose} = useDisclosure()

    const {
        app: {SMS_OPTIN_LOYALTY_ENABLED, SFSC_EMAIL_SUBSCRIPTION_ENABLED}
    } = getConfig()
    const isSMSOptInEnabled = SMS_OPTIN_LOYALTY_ENABLED === 'true'
    const isSfscEmailSubscriptionEnabled = SFSC_EMAIL_SUBSCRIPTION_ENABLED == 'true'
    const customerId = useCustomerId()
    const {data: customer} = useCustomer({parameters: {customerId}}, {enabled: !!customerId})
    const {mutate: updateEmailPreferences, isLoading} =
        useShopperCustomersMutation('updateCustomer')

    const smsMessages = defineMessages({
        subscribe: {
            id: 'communication_preferences.sms.subscribe',
            defaultMessage: 'Subscribe'
        },
        subscribed: {
            id: 'communication_preferences.sms.subscribed',
            defaultMessage: 'Subscribed'
        }
    })

    const form = useForm({
        defaultValues: {
            [`${prefix}emailGarageOptIn`]: 'true',
            [`${prefix}emailGaragePersonalizedOptIn`]: 'true'
        }
    })

    const fields = useCommunicationsFields({form, prefix})

    const headingRef = useRef()

    const device = usedDeviceDetection()

    useEffect(() => {
        headingRef?.current?.focus()
    }, [])

    useEffect(() => {
        if (customer) {
            form.reset({
                [`${prefix}emailGarageOptIn`]: String(customer.c_email_garage_optedIn ?? true),
                [`${prefix}emailGaragePersonalizedOptIn`]: String(
                    customer.c_email_garage_personalized_optedIn ?? true
                )
            })
        }
    }, [customer, prefix, form])

    const onSubmit = ({emailGarageOptIn, emailGaragePersonalizedOptIn}) => {
        updateEmailPreferences(
            {
                parameters: {
                    customerId
                },
                body: {
                    c_email_garage_optedIn: emailGarageOptIn === 'true',
                    c_email_garage_personalized_optedIn: emailGaragePersonalizedOptIn === 'true',
                    c_device: device
                }
            },
            {
                onError() {
                    // GTM Modification
                    sendViewErrorEvent('Failed to update email preferences. Please try again.')
                    form.setError('global', {
                        type: 'manual',
                        message: formatMessage({
                            id: 'communication_preferences.email.error_message',
                            defaultMessage: 'Failed to update email preferences. Please try again.'
                        })
                    })
                }
            }
        )
    }

    return (
        <Box mx="auto">
            <Stack spacing={8}>
                <Heading
                    as="h1"
                    fontSize={{base: '20px', lg: '24px'}}
                    fontWeight="bold"
                    textTransform="uppercase"
                    tabIndex="0"
                    ref={headingRef}
                    letterSpacing="wide"
                >
                    <Flex alignItems="center" gap="25px">
                        <Link
                            display={{base: 'flex', lg: 'none'}}
                            href="/account"
                            alignItems="center"
                        >
                            <LeftArrow />
                        </Link>
                        <Text
                            textOverflow="ellipsis"
                            overflow="hidden"
                            whiteSpace="nowrap"
                            pr={{base: '50px', lg: 0}}
                        >
                            <FormattedMessage
                                id="account_detail.communication_preferences"
                                defaultMessage="Communication Preferences"
                            />
                        </Text>
                    </Flex>
                </Heading>

                {form.formState.errors?.global && (
                    <Alert status="error" maxW={'max-content'}>
                        <AlertIcon color="red.500" boxSize={4} />
                        <Text fontSize="sm" ml={3}>
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}

                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Stack spacing={10}>
                        {isSfscEmailSubscriptionEnabled && (
                            <Box>
                                <Heading
                                    as="h3"
                                    fontSize="md"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    mb={4}
                                >
                                    <FormattedMessage
                                        id="communication_preferences.email.title"
                                        defaultMessage="Email"
                                    />
                                </Heading>

                                {/* Email General */}
                                <Box mb={6}>
                                    <Text mb={2} fontSize="md">
                                        <FormattedMessage
                                            id="communication_preferences.email.general.description"
                                            defaultMessage="Receive special offers, store news and updates on the latest fashion trends"
                                        />
                                    </Text>
                                    <Field {...fields.emailGarageOptIn} />
                                </Box>

                                {/* Email Garage */}
                                <Box>
                                    <Text mb={2} fontSize="md">
                                        <FormattedMessage
                                            id="communication_preferences.email.garage.description"
                                            defaultMessage="Receive Garage Collectif notifications and special personalized recommendations specifically tailored for you"
                                        />
                                    </Text>
                                    <Field {...fields.emailGaragePersonalizedOptIn} />
                                </Box>
                            </Box>
                        )}

                        {isSMSOptInEnabled && (
                            <Box>
                                <Heading
                                    as="h3"
                                    fontSize="md"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    mb={4}
                                >
                                    <FormattedMessage
                                        id="communication_preferences.sms.title"
                                        defaultMessage="SMS & Push Notifications"
                                    />
                                </Heading>

                                <Text mb={4} fontSize="md">
                                    <FormattedMessage
                                        id="communication_preferences.sms.description"
                                        defaultMessage="Receive special offers, store news and updates on the latest fashion trends"
                                    />
                                </Text>

                                <Button
                                    variant="solid"
                                    py={3}
                                    px={6}
                                    onClick={onOpen}
                                    fontSize="sm"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    mb={6}
                                    isDisabled={customer?.c_sms_garage_optedIn}
                                >
                                    {formatMessage(
                                        customer?.c_sms_garage_optedIn
                                            ? smsMessages.subscribed
                                            : smsMessages.subscribe
                                    )}
                                </Button>

                                <Text fontSize="xs">
                                    <FormattedMessage
                                        id="communication_preferences.sms.disclaimer"
                                        defaultMessage="To unsubscribe reply STOP at any time. Message frequency varies. Standard message & data rates may apply."
                                    />
                                </Text>
                            </Box>
                        )}

                        <Box>
                            <Button
                                variant="solid"
                                type="submit"
                                fontSize="md"
                                fontWeight="bold"
                                textTransform="uppercase"
                                width={{base: 'full', lg: '345px'}}
                                mb={4}
                                onClick={() => {
                                    form.clearErrors('global')
                                }}
                                isLoading={isLoading}
                                loadingText={formatMessage({
                                    id: 'communication_preferences.sms.saving',
                                    defaultMessage: 'Saving...'
                                })}
                            >
                                <FormattedMessage
                                    id="communication_preferences.save_changes"
                                    defaultMessage="Save Changes"
                                />
                            </Button>

                            <Text fontSize="xs">
                                <FormattedMessage
                                    id="communication_preferences.transactional_note"
                                    defaultMessage="Note: You cannot unsubscribe from transactional emails (including order confirmation or cancellation emails)."
                                />
                            </Text>
                        </Box>
                    </Stack>
                </form>
            </Stack>
            {/* Removing for MVP */}
            {/* {isSMSOptInEnabled && <SMSOptInModal isOpen={isOpen} onClose={onClose} />} */}
        </Box>
    )
}

CommunicationPreferences.propTypes = {
    prefix: PropTypes.string
}

export default CommunicationPreferences
